import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import { Box, Button, Container, IconButton, Menu, MenuItem, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { type MouseEvent, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { usePortalAuth } from '../context/PortalAuthContext';

type AppNavbarMode = 'landing' | 'app';

interface AppNavbarProps {
  mode: AppNavbarMode;
  onHomeClick?: () => void;
  onAboutClick?: () => void;
  onContactClick?: () => void;
}

function AppNavbar({ mode, onHomeClick, onAboutClick, onContactClick }: AppNavbarProps) {
  const theme = useTheme();
  const { almathema } = theme.palette;
  const { user, signOut } = usePortalAuth();
  const userDisplayName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.email || 'User';
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [profileMenuAnchorEl, setProfileMenuAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navBackgroundColor = theme.palette.secondary.main;
  const navBackgroundImage = `linear-gradient(90deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`;
  const navBackdropFilter = 'none';
  const navPaddingY = isScrolled ? 1 : 2;
  const logoWidth = isScrolled ? { xs: 130, md: 180 } : { xs: 150, md: 210 };
  const isMenuOpen = Boolean(menuAnchorEl);
  const isProfileMenuOpen = Boolean(profileMenuAnchorEl);
  const mobileMenuPaperSx = {
    mt: 1,
    left: '0 !important',
    width: '100vw',
    maxWidth: '100vw',
    borderRadius: 0,
    backgroundColor: navBackgroundColor,
    backgroundImage: navBackgroundImage,
    color: almathema.white,
  };
  const mobileMenuListSx = {
    py: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    '& .MuiMenuItem-root': {
      color: almathema.white,
      fontSize: '0.9375rem',
      minHeight: 44,
      width: '100%',
      justifyContent: 'flex-end',
      textAlign: 'right',
    },
    '& .MuiMenuItem-root:hover': {
      backgroundColor: almathema.whiteOverlay12,
    },
  };

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleProfileMenuToggle = (event: MouseEvent<HTMLElement>) => {
    if (isProfileMenuOpen) {
      setProfileMenuAnchorEl(null);
      return;
    }
    setProfileMenuAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchorEl(null);
  };

  const handleAccountNavigation = () => {
    handleMenuClose();
    handleProfileMenuClose();
    navigate('/account');
  };

  const handleSignOut = async () => {
    handleMenuClose();
    handleProfileMenuClose();
    await signOut();
    navigate('/');
  };

  if (mode === 'landing') {
    return (
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1200,
          backgroundColor: navBackgroundColor,
          backgroundImage: navBackgroundImage,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backdropFilter: navBackdropFilter,
          transition: 'background-color 220ms ease, backdrop-filter 220ms ease',
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            py={navPaddingY}
            sx={{ transition: 'padding 220ms ease' }}
          >
            <Box
              component="img"
              src="/logo-almathema.svg"
              alt="alMATHEMA"
              sx={{ width: logoWidth, height: 'auto', transition: 'width 220ms ease' }}
            />

            <Stack direction="row" spacing={2} alignItems="center" sx={{ display: { xs: 'none', md: 'flex' } }}>
              <Button
                component={RouterLink}
                to="/"
                size="small"
                onClick={onHomeClick}
                sx={{ color: almathema.white, fontSize: '0.9375rem', '&:hover': { backgroundColor: almathema.whiteOverlay12 } }}
              >
                Home
              </Button>
              <Button
                size="small"
                sx={{ color: almathema.white, fontSize: '0.9375rem', '&:hover': { backgroundColor: almathema.whiteOverlay12 } }}
                onClick={onAboutClick}
              >
                About Us
              </Button>
              <Button
                size="small"
                sx={{ color: almathema.white, fontSize: '0.9375rem', '&:hover': { backgroundColor: almathema.whiteOverlay12 } }}
                onClick={onContactClick}
              >
                Contact Us
              </Button>
              <Button
                component={RouterLink}
                to="/signin"
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: almathema.sunYellow,
                  color: almathema.sapphire,
                  fontSize: '0.9375rem',
                  '&:hover': { backgroundColor: almathema.sunYellowHover },
                }}
              >
                Sign In
              </Button>
            </Stack>

            <IconButton
              size="small"
              aria-label="Open navigation menu"
              onClick={handleMenuOpen}
              sx={{
                display: { xs: 'flex', md: 'none' },
                color: almathema.white,
                border: `1px solid ${almathema.white}`,
                borderRadius: 1,
              }}
            >
              <MenuIcon fontSize="small" />
            </IconButton>

            <Menu
              anchorEl={menuAnchorEl}
              open={isMenuOpen}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              sx={{ display: { xs: 'block', md: 'none' }}}
              PaperProps={{ sx: mobileMenuPaperSx }}
              MenuListProps={{ sx: mobileMenuListSx }}
            >
              <MenuItem
                component={RouterLink}
                to="/"
                onClick={() => {
                  onHomeClick?.();
                  handleMenuClose();
                }}
              >
                Home
              </MenuItem>
              <MenuItem
                onClick={() => {
                  onAboutClick?.();
                  handleMenuClose();
                }}
              >
                About Us
              </MenuItem>
              <MenuItem
                onClick={() => {
                  onContactClick?.();
                  handleMenuClose();
                }}
              >
                Contact Us
              </MenuItem>
              <MenuItem
                component={RouterLink}
                to="/signin"
                onClick={handleMenuClose}
                sx={{
                  backgroundColor: almathema.sunYellow,
                  color: `${almathema.sapphire} !important`,
                  fontWeight: 700,
                  '&:hover': {
                    backgroundColor: `${almathema.sunYellowHover} !important`,
                  },
                }}
              >
                Sign In
              </MenuItem>
            </Menu>
          </Stack>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1200,
        backgroundColor: navBackgroundColor,
        backgroundImage: navBackgroundImage,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backdropFilter: navBackdropFilter,
        transition: 'background-color 220ms ease, backdrop-filter 220ms ease',
      }}
    >
      <Container maxWidth="lg">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          py={navPaddingY}
          sx={{ transition: 'padding 220ms ease' }}
        >
          <Box component={RouterLink} to="/" sx={{ display: 'inline-flex' }}>
            <Box
              component="img"
              src="/logo-almathema.svg"
              alt="alMATHEMA"
              sx={{ width: logoWidth, height: 'auto', transition: 'width 220ms ease' }}
            />
          </Box>

          <Stack direction="row" spacing={2} alignItems="center" sx={{ display: { xs: 'none', md: 'flex' } }}>
            <Button
              component={RouterLink}
              to="/"
              size="small"
              onClick={onHomeClick}
              sx={{ color: almathema.white, fontSize: '0.9375rem', '&:hover': { backgroundColor: almathema.whiteOverlay12 } }}
            >
              Home
            </Button>

          {!user ? (
            <>
              <Button
                component={RouterLink}
                to="/signin"
                size="small"
                sx={{ color: almathema.white, fontSize: '0.9375rem', '&:hover': { backgroundColor: almathema.whiteOverlay12 } }}
              >
                Sign In
              </Button>
              <Button
                component={RouterLink}
                to="/register"
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: almathema.sunYellow,
                  color: almathema.sapphire,
                  fontSize: '0.9375rem',
                  '&:hover': { backgroundColor: almathema.sunYellowHover },
                }}
              >
                Register
              </Button>
            </>
          ) : (
            <>
              {user.role === 'student' ? (
                <>
                  <Button
                    component={RouterLink}
                    to="/student-portal"
                    size="small"
                    sx={{ color: almathema.white, fontSize: '0.9375rem', '&:hover': { backgroundColor: almathema.whiteOverlay12 } }}
                  >
                    Student Portal
                  </Button>
                </>
              ) : user.role === 'admin' ? (
                <Button
                  component={RouterLink}
                  to="/admin-portal"
                  size="small"
                  sx={{ color: almathema.white, fontSize: '0.9375rem', '&:hover': { backgroundColor: almathema.whiteOverlay12 } }}
                >
                  Admin Portal
                </Button>
              ) : (
                <Button
                  component={RouterLink}
                  to="/educator-portal"
                  size="small"
                  sx={{ color: almathema.white, fontSize: '0.9375rem', '&:hover': { backgroundColor: almathema.whiteOverlay12 } }}
                >
                  Educator Portal
                </Button>
              )}
              <Button
                size="small"
                onClick={handleProfileMenuToggle}
                startIcon={<AccountCircleOutlinedIcon fontSize="small" />}
                sx={{
                  color: almathema.white,
                  fontSize: '0.9375rem',
                  textTransform: 'none',
                  maxWidth: 220,
                  '&:hover': { backgroundColor: almathema.whiteOverlay12 },
                }}
                title={userDisplayName}
              >
                <Box
                  component="span"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'inline-block',
                    maxWidth: 160,
                  }}
                >
                  {userDisplayName}
                </Box>
              </Button>
              <Menu
                anchorEl={profileMenuAnchorEl}
                open={isProfileMenuOpen}
                onClose={handleProfileMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={handleAccountNavigation}>Account</MenuItem>
                <MenuItem onClick={() => void handleSignOut()}>Sign Out</MenuItem>
              </Menu>
            </>
          )}
          </Stack>

          <IconButton
            size="small"
            aria-label="Open navigation menu"
            onClick={handleMenuOpen}
            sx={{
              display: { xs: 'flex', md: 'none' },
              color: almathema.white,
              border: `1px solid ${almathema.white}`,
              borderRadius: 1,
            }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>

          <Menu
            anchorEl={menuAnchorEl}
            open={isMenuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{ display: { xs: 'block', md: 'none' } }}
            PaperProps={{ sx: mobileMenuPaperSx }}
            MenuListProps={{ sx: mobileMenuListSx }}
          >
            <MenuItem
              component={RouterLink}
              to="/"
              onClick={() => {
                onHomeClick?.();
                handleMenuClose();
              }}
            >
              Home
            </MenuItem>

            {!user ? (
              [
                <MenuItem
                  key="signin"
                  component={RouterLink}
                  to="/signin"
                  onClick={handleMenuClose}
                >
                  Sign In
                </MenuItem>,
                <MenuItem 
                  key="register" 
                  component={RouterLink} 
                  to="/register" 
                  onClick={handleMenuClose}
                  sx={{
                    backgroundColor: almathema.sunYellow,
                    color: `${almathema.sapphire} !important`,
                    fontWeight: 700,
                    '&:hover': {
                      backgroundColor: almathema.sunYellowHover,
                    },
                  }}
                  >
                  Register
                </MenuItem>,
              ]
            ) : user.role === 'student' ? (
              [
                <MenuItem key="current-user" disabled>
                  {userDisplayName}
                </MenuItem>,
                <MenuItem key="student-portal" component={RouterLink} to="/student-portal" onClick={handleMenuClose}>
                  Student Portal
                </MenuItem>,
                <MenuItem key="account" onClick={handleAccountNavigation}>
                  Account
                </MenuItem>,
                <MenuItem key="signout" onClick={() => void handleSignOut()}>
                  Sign Out
                </MenuItem>,
              ]
            ) : user.role === 'admin' ? (
              [
                <MenuItem key="current-user" disabled>
                  {userDisplayName}
                </MenuItem>,
                <MenuItem key="admin-portal" component={RouterLink} to="/admin-portal" onClick={handleMenuClose}>
                  Admin Portal
                </MenuItem>,
                <MenuItem key="account" onClick={handleAccountNavigation}>
                  Account
                </MenuItem>,
                <MenuItem key="signout" onClick={() => void handleSignOut()}>
                  Sign Out
                </MenuItem>,
              ]
            ) : (
              [
                <MenuItem key="current-user" disabled>
                  {userDisplayName}
                </MenuItem>,
                <MenuItem key="educator-portal" component={RouterLink} to="/educator-portal" onClick={handleMenuClose}>
                  Educator Portal
                </MenuItem>,
                <MenuItem key="account" onClick={handleAccountNavigation}>
                  Account
                </MenuItem>,
                <MenuItem key="signout" onClick={() => void handleSignOut()}>
                  Sign Out
                </MenuItem>,
              ]
            )}
          </Menu>
        </Stack>
      </Container>
    </Box>
  );
}

export default AppNavbar;
